import React from 'react';

export type ViewMode = 'pipeline' | 'checkin' | 'source' | 'activity';
export type PipelineStatus = 'invited' | 'opened' | 'registered' | 'approved' | 'rejected' | 'declined' | 'cancelled' | 'no_show';
export type CheckinFilter = 'checked_in' | 'not_checked_in' | 'no_show';
export type SourceFilter = 'all' | 'invitation' | 'direct';

interface FilterTab {
  label: string;
  value: string;
  count: number;
  color?: string;
}

interface AttendeeFiltersProps {
  viewMode: ViewMode;
  pipelineFilter: 'all' | PipelineStatus;
  checkinFilter: CheckinFilter;
  sourceFilter: SourceFilter;
  sortOrder: 'newest' | 'oldest';
  counts: {
    all: number;
    invited: number;
    opened: number;
    registered: number;
    approved: number;
    rejected: number;
    declined: number;
    cancelled: number;
    noShow: number;
    checkedIn: number;
    notCheckedIn: number;
    invitationBased: number;
    directRegistrations: number;
  };
  onPipelineFilterChange: (filter: 'all' | PipelineStatus) => void;
  onCheckinFilterChange: (filter: CheckinFilter) => void;
  onSourceFilterChange: (filter: SourceFilter) => void;
  onSortOrderToggle: () => void;
}

const AttendeeFilters: React.FC<AttendeeFiltersProps> = ({
  viewMode,
  pipelineFilter,
  checkinFilter,
  sourceFilter,
  sortOrder,
  counts,
  onPipelineFilterChange,
  onCheckinFilterChange,
  onSourceFilterChange,
  onSortOrderToggle,
}) => {
  const pipelineTabs: FilterTab[] = [
    { label: 'All Requests', value: 'all', count: counts.all },
    { label: 'Invited', value: 'invited', count: counts.invited, color: 'blue' },
    { label: 'Opened', value: 'opened', count: counts.opened, color: 'indigo' },
    { label: 'Registered', value: 'registered', count: counts.registered, color: 'purple' },
    { label: 'Approved', value: 'approved', count: counts.approved, color: 'green' },
    { label: 'Rejected', value: 'rejected', count: counts.rejected, color: 'red' },
    { label: 'Declined', value: 'declined', count: counts.declined, color: 'gray' },
    { label: 'Cancelled', value: 'cancelled', count: counts.cancelled, color: 'gray' },
    { label: 'No-show', value: 'no_show', count: counts.noShow, color: 'red' },
  ];

  const checkinTabs: FilterTab[] = [
    { label: 'Not Checked In', value: 'not_checked_in', count: counts.notCheckedIn, color: 'orange' },
    { label: 'Checked In', value: 'checked_in', count: counts.checkedIn, color: 'green' },
    { label: 'No-show', value: 'no_show', count: counts.noShow, color: 'red' },
  ];

  const sourceTabs: FilterTab[] = [
    { label: 'All Sources', value: 'all', count: counts.all },
    { label: 'Manual Invitations', value: 'invitation', count: counts.invitationBased, color: 'blue' },
    { label: 'Direct Registrations', value: 'direct', count: counts.directRegistrations, color: 'green' },
  ];

  const renderFilterTabs = (
    tabs: FilterTab[],
    activeFilter: string,
    onFilterChange: (value: any) => void
  ) => (
    <div className="flex flex-wrap gap-2 items-center">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          onClick={() => {
            if (activeFilter === tab.value) {
              onSortOrderToggle();
            } else {
              onFilterChange(tab.value);
            }
          }}
          className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 text-sm flex-shrink-0 ${
            activeFilter === tab.value
              ? `bg-${tab.color || 'gray'}-100 text-${tab.color || 'gray'}-700`
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <span className="whitespace-nowrap">{tab.label}</span>
          <span className={`text-xs ${
            activeFilter === tab.value ? 'font-bold' : ''
          }`}>
            ({tab.count})
          </span>
          {activeFilter === tab.value && (
            <span className="text-xs">
              {sortOrder === 'newest' ? '↓' : '↑'}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="mb-6">
      {viewMode === 'pipeline' && renderFilterTabs(pipelineTabs, pipelineFilter, onPipelineFilterChange)}
      {viewMode === 'checkin' && renderFilterTabs(checkinTabs, checkinFilter, onCheckinFilterChange)}
      {viewMode === 'source' && renderFilterTabs(sourceTabs, sourceFilter, onSourceFilterChange)}
    </div>
  );
};

export default AttendeeFilters;